import { useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupsIcon from "@mui/icons-material/Groups";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRowSelectionModel } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { downloadBulkCvs, listPractitioners } from "@/api/practitioners";
import type { Practitioner } from "@/api/types";
import { usePermission } from "@/auth/usePermission";
import { BulkAddMembershipDialog } from "@/components/BulkAddMembershipDialog";
import { BulkArchiveDialog } from "@/components/BulkArchiveDialog";
import { ImportPractitionersDialog } from "@/components/ImportPractitionersDialog";
import { MembershipsPanel } from "@/components/MembershipsPanel";
import { PageHeader } from "@/components/PageHeader";
import { PractitionerCardGrid } from "@/components/PractitionerCardGrid";
import { PractitionerFormDrawer } from "@/components/PractitionerFormDrawer";
import { StatusChip } from "@/components/StatusChip";
import { loadView, saveView, ViewToggle, type ListView } from "@/components/ViewToggle";

export function PractitionersListPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const canCreate = usePermission("practitioner:create");
  const canUpdate = usePermission("practitioner:update");
  const canBulk = usePermission("practitioner:update");
  const canAddMembership = usePermission("membership:create");

  const [q, setQ] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [editing, setEditing] = useState<Practitioner | "new" | null>(null);
  const [showMemberships, setShowMemberships] = useState<Practitioner | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [view, setView] = useState<ListView>(() => loadView("practitioners"));
  const changeView = (v: ListView) => {
    setView(v);
    saveView("practitioners", v);
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["practitioners", q, paginationModel.page, paginationModel.pageSize],
    queryFn: () =>
      listPractitioners({
        q: q || undefined,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
      }),
  });

  const columns: GridColDef<Practitioner>[] = useMemo(
    () => [
      { field: "display_name", headerName: t("practitioners.lastName"), minWidth: 180, flex: 2 },
      { field: "rpps", headerName: t("practitioners.rpps"), minWidth: 130, flex: 1 },
      { field: "email", headerName: t("practitioners.email"), minWidth: 200, flex: 2 },
      { field: "phone", headerName: t("practitioners.phone"), minWidth: 140, flex: 1 },
      {
        field: "membership_kinds",
        headerName: t("practitioners.kind"),
        minWidth: 150,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
            {(row.membership_kinds ?? []).map((k) => (
              <Chip
                key={k}
                size="small"
                variant="outlined"
                color={k === "LIBERAL" ? "secondary" : "default"}
                label={t(`membershipKind.${k}`, { defaultValue: k })}
              />
            ))}
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: t("practitioners.status"),
        minWidth: 120,
        flex: 1,
        renderCell: ({ row }) => <StatusChip value={row.status} />,
      },
      {
        field: "actions",
        headerName: t("orgs.actions"),
        width: 110,
        sortable: false,
        filterable: false,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={t("practitioners.memberships")}>
              <IconButton size="small" onClick={() => setShowMemberships(row)}>
                <GroupsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canUpdate && (
              <Tooltip title={t("practitioners.edit")}>
                <IconButton size="small" onClick={() => setEditing(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [t, canUpdate],
  );

  return (
    <Box>
      <PageHeader
        helpTerm="praticien"
        title={t("practitioners.title")}
        subtitle={t("practitioners.subtitle")}
        actions={
          <>
            <ViewToggle view={view} onChange={changeView} />
            <Tooltip title={t("common.retry")}>
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => setShowImport(true)}
              >
                {t("practitioners.importCsv")}
              </Button>
            )}
            {canCreate && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    const blob = await downloadBulkCvs("ACTIVE");
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `cortex-cvs-${new Date().toISOString().slice(0, 10)}.zip`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }}
              >
                {t("practitioners.downloadBulkCvs")}
              </Button>
            )}
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEditing("new")}
              >
                {t("practitioners.create")}
              </Button>
            )}
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t("practitioners.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: { xs: "100%", sm: 320 } }}
        />
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      {selection.length > 0 && (canBulk || canAddMembership) && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {t("practitionersBulk.selectedCount", { count: selection.length })}
          </Typography>
          {canAddMembership && (
            <Button
              size="small"
              variant="contained"
              color="inherit"
              startIcon={<GroupAddIcon />}
              onClick={() => setBulkAddOpen(true)}
              sx={{ color: "primary.main" }}
            >
              {t("practitionersBulk.addMembershipCta")}
            </Button>
          )}
          {canBulk && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<ArchiveIcon />}
              onClick={() => setBulkArchiveOpen(true)}
            >
              {t("practitionersBulk.archiveCta")}
            </Button>
          )}
          <Button size="small" color="inherit" onClick={() => setSelection([])}>
            {t("common.cancel")}
          </Button>
        </Paper>
      )}

      {view === "table" ? (
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={data?.items ?? []}
            columns={columns}
            rowCount={data?.total ?? 0}
            loading={isLoading}
            getRowId={(row) => row.id}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={canBulk || canAddMembership}
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            keepNonExistentRowsSelected
            disableRowSelectionOnClick
            density="comfortable"
          />
        </Box>
      ) : (
        <PractitionerCardGrid
          rows={data?.items ?? []}
          loading={isLoading}
          selection={new Set(selection.map(String))}
          onToggleSelect={(id) => {
            const next = new Set(selection.map(String));
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setSelection(Array.from(next) as unknown as GridRowSelectionModel);
          }}
          onShowMemberships={setShowMemberships}
          onEdit={setEditing}
          showSelection={canBulk || canAddMembership}
          canUpdate={canUpdate}
        />
      )}

      <PractitionerFormDrawer
        open={editing !== null}
        practitioner={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["practitioners"] });
        }}
      />

      <MembershipsPanel
        open={showMemberships !== null}
        practitioner={showMemberships}
        onClose={() => setShowMemberships(null)}
      />

      <ImportPractitionersDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => qc.invalidateQueries({ queryKey: ["practitioners"] })}
      />

      <BulkArchiveDialog
        open={bulkArchiveOpen}
        ids={selection.map(String)}
        onClose={() => setBulkArchiveOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["practitioners"] });
          setSelection([]);
        }}
      />

      <BulkAddMembershipDialog
        open={bulkAddOpen}
        ids={selection.map(String)}
        onClose={() => setBulkAddOpen(false)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ["memberships"] });
          setSelection([]);
        }}
      />
    </Box>
  );
}
